import React, { useEffect } from 'react';
import classnames from 'classnames';
import flatten from 'lodash/flatten';
import reverse from 'lodash/reverse';
import some from 'lodash/some';
import sortBy from 'lodash/sortBy';
import { useTranslation } from 'react-i18next';

import { Cell } from 'formlabs-web-components/Grid';
import { Announcement } from 'formlabs-web-components/Type';
import Product from './Product';
import useFetchProducts from './useFetchProducts';

import './Products.module.css';

const sort = {
  default: products => sortBy(products, ['position', 'id']),
  alpha: products => sortBy(products, 'name'),
  date: products => reverse(sortBy(products, ['created_at', 'position'])),
  priceDesc: products => reverse(sortBy(products, 'price')),
  priceAsc: products => sortBy(products, 'price')
};

const Products = ({
  activeFilterCount,
  category,
  displayStyle = 'line',
  filters,
  order,
  resetFilterAction,
  setProductCount,
  storeId,
  vertical = 'flat'
}) => {
  const { t } = useTranslation();
  const { loading, data } = useFetchProducts(category, storeId, vertical);

  // filter
  const filteredProducts = activeFilterCount
    ? data.filter(product => {
        const matchedFilters = Object.keys(filters).map(filterName => {
          const options = filters[filterName].map(
            option =>
              product.attributes[filterName] &&
              product.attributes[filterName].includes(option)
          );
          return options.length ? some(options) : options; // Logical OR over options
        });
        return flatten(matchedFilters).every(Boolean); // Logical AND over filters
      })
    : data;

  // sort
  const products = sort[order](filteredProducts);

  const productCount = products.length;
  useEffect(
    () => {
      setProductCount(productCount);
    },
    [productCount]
  );

  if (loading || !storeId) {
    return Array.from(Array(displayStyle === 'grid' ? 9 : 6), (v, i) => (
      <Cell mediumSize={displayStyle === 'list' ? 24 : 8} key={i}>
        <Product empty displayStyle={displayStyle} />
      </Cell>
    ));
  }

  if (!products.length) {
    return (
      <Cell mediumSize="24">
        <Announcement>
          {t('No products were found matching your selection.')}{' '}
          <button onClick={resetFilterAction}>
            {t('Clear your selection')}
          </button>
        </Announcement>
      </Cell>
    );
  }

  return products.map(product => (
    <Cell
      mediumSize={displayStyle === 'list' ? 24 : 8}
      key={product.sku}
      styleName={classnames({ 'is-grid': displayStyle === 'grid' })}
    >
      <Product product={product} displayStyle={displayStyle} />
    </Cell>
  ));
};

export default Products;
